import { getNonUmbracoUrl } from "./getNonUmbracoUrl"
import { getUmbracoMediaUrl } from "./getUmbracoMediaUrl"

/**
 * Replaces all <a href="..."> links and <img src="..."> tags in an HTML string.
 * - For links: processes href with getNonUmbracoUrl and getUmbracoMediaUrl
 * - For images: processes src with getUmbracoMediaUrl
 * @param html The HTML string to process
 * @returns The HTML string with updated <a> hrefs and <img> src attributes
 */
export function replaceRichTextLinks(
  html?: string,
  replaceStrong?: "h2" | "h4",
): string {
  if (!html || typeof html !== "string") return ""

  // Process <a> tags
  let processedHtml = html.replace(
    /<a\s+([^>]*href=["'])([^"'>]+)(["'][^>]*)>/gi,
    (match, beforeHref, href, afterHref) => {
      let newHref = getNonUmbracoUrl(href, true)
      newHref = getUmbracoMediaUrl(newHref)
      const GREEN_LINK_COLOR = "#008761" // brandDark
      let attributes = `${beforeHref}${newHref}${afterHref}`

      // Ensure we manipulate (or add) a style attribute for consistent link styling
      const styleAttrRegex = /style=(["'])(.*?)\1/i
      const desiredDeclarations = ["color:#008761", "text-decoration:underline"]

      if (styleAttrRegex.test(attributes)) {
        attributes = attributes.replace(
          styleAttrRegex,
          (styleMatch, quote, content) => {
            // Remove any existing color or text-decoration declarations
            let cleaned = content
              .replace(/color\s*:[^;]+;?/gi, "")
              .replace(/text-decoration\s*:[^;]+;?/gi, "")
              .trim()

              // Collapse multiple spaces and ensure trailing semicolons
              .replace(/\s{2,}/g, " ")

            const finalContent =
              [
                ...desiredDeclarations,
                cleaned, // keep remaining styles (could be empty)
              ]
                .filter(Boolean)
                .join(";")
                .replace(/;+$/g, "") + ";"

            return `style=${quote}${finalContent}${quote}`
          },
        )
      } else {
        // Append new style attribute just before the closing angle bracket (we are still inside replacement before adding '>')
        attributes = `${attributes} style=\"${desiredDeclarations.join(";")};\"`
      }

      // Avoid double application if function is run again: remove duplicate style declarations (idempotency)
      attributes = attributes.replace(
        /(color:#008761;text-decoration:underline;)(?=(?:[^"']|"[^"]*"|'[^']*')*\1)/g,
        "$1",
      )

      return `<a ${attributes}>`
    },
  )

  // Replace line separator characters with spaces
  processedHtml = processedHtml.replace(/\u2028/g, " ")

  // Process <img> tags
  processedHtml = processedHtml.replace(
    /<img\s+([^>]*src=["'])([^"'>]+)(["'][^>]*)>/gi,
    (match, beforeSrc, src, afterSrc) => {
      const newSrc = getUmbracoMediaUrl(src)
      return `<img ${beforeSrc}${newSrc}${afterSrc}>`
    },
  )

  // Remove aria-level attribute from <li> tags
  processedHtml = processedHtml.replace(
    /<li\s+([^>]*)\s*aria-level=["'][^"']*["']\s*([^>]*)>/gi,
    (match, beforeAriaLevel, afterAriaLevel) => {
      const cleanedAttributes = `${beforeAriaLevel} ${afterAriaLevel}`.trim()
      return `<li${cleanedAttributes ? ` ${cleanedAttributes}` : ""}>`
    },
  )

  // Strip inline style attributes from pasted content so component CSS controls
  // typography and layout. Umbraco RTE content often has copy-pasted HTML with
  // inline styles (e.g. font-weight:400, font-style:normal, fixed widths) that
  // override bold/italic CSS rules and cause layout issues like table overflow.
  // We preserve styles only on <a> tags (handled above with link color) and <img>.
  const stripStyleTags =
    "span|div|p|h[1-6]|ul|ol|li|table|thead|tbody|tfoot|tr|th|td" +
    "|colgroup|col|strong|em|b|i|u|blockquote|section|article" +
    "|header|footer|nav|figure|figcaption|details|summary"
  // EGDE-725: `text-align` survives the strip. Unlike the pasted junk this
  // defence exists for, alignment is set by the RTE's own toolbar — it is the
  // editor's explicit choice, and inline style is the only place the RTE
  // records it.
  processedHtml = processedHtml.replace(
    new RegExp(
      `<(${stripStyleTags})(\\s[^>]*)\\bstyle=(["'])((?:(?!\\3).)*)\\3([^>]*)>`,
      "gi",
    ),
    (match, tag, before, quote, styleContent, after) => {
      const textAlign = /text-align\s*:\s*[^;]+/i.exec(styleContent)?.[0]
      const kept = textAlign ? ` style=${quote}${textAlign.trim()};${quote}` : ""
      const attrs = `${before}${after}`.replace(/\s{2,}/g, " ").trim()
      if (!attrs && !kept) return `<${tag}>`
      return `<${tag}${attrs ? ` ${attrs}` : ""}${kept}>`
    },
  )

  // Remove empty <div> elements with MUI classes (artifacts from copy-pasted content)
  processedHtml = processedHtml.replace(
    /<div\s+[^>]*class="[^"]*(?:muirtl-|MuiTypography)[^"]*"[^>]*>\s*<\/div>/gi,
    "",
  )

  // Replace <td> with <th> in the first row (header) of tables
  processedHtml = processedHtml.replace(
    /<tbody>\s*<tr>([\s\S]*?)<\/tr>/gi,
    (match, firstRowContent) => {
      // Replace opening <td> tags with <th>
      let updatedContent = firstRowContent.replace(/<td(\s|>)/gi, "<th$1")
      // Replace closing </td> tags with </th>
      updatedContent = updatedContent.replace(/<\/td>/gi, "</th>")
      return `<tbody><tr>${updatedContent}</tr>`
    },
  )

  // EGDE-726: the page's only H1 belongs to the hero blocks. Rich text is body
  // content wherever it renders, so an editor-typed (or pasted) <h1> becomes
  // an <h2> — same look (the renderers style both), correct outline.
  processedHtml = processedHtml
    .replace(/<h1(\s[^>]*)?>/gi, "<h2$1>")
    .replace(/<\/h1>/gi, "</h2>")

  // Replace <strong> tags with the specified heading tag if replaceStrong is provided
  if (replaceStrong) {
    processedHtml = processedHtml.replace(
      /<strong([^>]*)>(.*?)<\/strong>/gi,
      (match, attributes, content) => {
        return `<${replaceStrong}${attributes}>${content}</${replaceStrong}>`
      },
    )
  }

  return processedHtml
}
